import { FC, useEffect, useState } from "react";
import { useAppDispatch } from "hooks";
import { setPageTitle, setPageType } from "store";
import { PageType } from "types";
import styles from "./styles.module.scss";
import ImageUploading, { ImageListType } from "react-images-uploading";
import { Loader } from "@progress/kendo-react-indicators";
import { Button } from "@progress/kendo-react-buttons";
import {
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { ScrollView } from "@progress/kendo-react-scrollview";

const DROPDOWN_OPTIONS = [
  {
    label: "Sample 1",
    value:
      "https://dreamdemoassets.blob.core.windows.net/openai/sample%20image%204.jpg",
  },
  {
    label: "Sample 2",
    value:
      "https://dreamdemoassets.blob.core.windows.net/openai/sample%20image%203.jpg",
  },
  {
    label: "Sample 3",
    value:
      "https://dreamdemoassets.blob.core.windows.net/openai/sample%20image%201.jpg",
  },
  {
    label: "Sample 4",
    value:
      "https://dreamdemoassets.blob.core.windows.net/openai/sample%20image%202.jpg",
  },
];

export const ProductRecommendation: FC = () => {
  const dispatch = useAppDispatch();

  const [images, setImages] = useState<ImageListType>([]);

  useEffect(() => {
    dispatch(setPageType(PageType.ProductRecommendation));
    dispatch(setPageTitle("Product Recommendation"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [result, setResult] = useState<any>({});

  const onChange = (imageList: ImageListType) => {
    setResult({});
    setIsProcessing(false);
    setImages(imageList as never[]);
  };

  const fetchData = (formData?: any) => {
    setIsLoading(true);
    fetch(
      `https://productrecommndationfromdescription.azurewebsites.net/api/RecommendingFromDesc`,
      {
        method: "post",
        ...(formData && { body: formData }),
      }
    )
      .then((res) => res.json())
      .then((res) => {
        setResult(res);
        setIsLoading(false);
      })
      .catch((e) => console.log(e));
  };

  const [isLoading, setIsLoading] = useState(false);

  const [value, setValue] = useState<{
    label: string;
    value: string;
  }>(DROPDOWN_OPTIONS[0]);

  const [isProcessing, setIsProcessing] = useState(false);

  const handleChange = (event: DropDownListChangeEvent) => {
    setIsProcessing(false);
    setValue(DROPDOWN_OPTIONS.filter((d) => d.label === event.target.value)[0]);
  };

  const onProcess = () => {
    setIsProcessing(true);
    if (images?.length) {
      const formData: any = new FormData();
      const fileData = images[0].file;
      formData.append("file", fileData);
      fetchData(formData);
    } else {
      const formData: any = new FormData();
      const image = value.value;
      formData.append("image_url", image);
      formData.append("number", "3");
      fetchData(
        JSON.stringify({
          image_url: image,
          number: "3",
        })
      );
    }
  };

  //preload images
  function preloadImage(src: string) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = function () {
        resolve(img);
      };
      img.onerror = img.onabort = function () {
        reject(src);
      };
      img.src = src;
    });
  }

  useEffect(() => {
    let isCancelled = false;

    async function effect() {
      if (isCancelled) {
        return;
      }

      const imagesPromiseList: Promise<any>[] = [];
      for (const i of DROPDOWN_OPTIONS) {
        imagesPromiseList.push(preloadImage(i.value));
      }

      await Promise.all(imagesPromiseList);

      if (isCancelled) {
        return;
      }
    }

    effect();

    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.dropDownContainer}>
        <DropDownList
          className={styles.dropDown}
          data={DROPDOWN_OPTIONS.map((d) => d.label)}
          value={value?.label}
          onChange={handleChange}
        />
        <Button className={styles.linkBtn}>
          <a href={value?.value} target="_blank" rel="noreferrer">
            Link
          </a>
        </Button>
      </div>

      <div className={styles.tabContainer}>
        <div className={styles.tabImg}>
          <ImageUploading
            value={images}
            onChange={onChange}
            allowNonImageType={false}
            multiple={false}
          >
            {({ imageList }) => {
              return (
                <div
                  className={`upload__image-wrapper ${styles.imageWrapper}`}
                  style={{ width: "100%", height: "100%" }}
                >
                  {imageList?.length > 0 && (
                    <span
                      onClick={() => {
                        !isLoading && setImages([]);
                        setValue(DROPDOWN_OPTIONS[0]);
                      }}
                      className={`k-icon k-i-close ${styles.closeBtn}`}
                    ></span>
                  )}
                  {imageList?.map((image, index) => (
                    <div
                      key={index}
                      className="image-item"
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "calc(100% - 48px)",
                      }}
                    >
                      <img
                        src={image.dataURL}
                        alt="uploaded-form"
                        style={{ height: "100%" }}
                      />
                    </div>
                  ))}
                  {images.length === 0 && (
                    <>
                      <img
                        src={value.value}
                        alt="uploaded-form"
                        style={{
                          height: "auto",
                          maxHeight: "calc(100% - 48px)",
                        }}
                      />
                    </>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <Button
                      className={styles.btn}
                      onClick={onProcess}
                      id="#file"
                    >
                      Process
                    </Button>
                  </div>
                </div>
              );
            }}
          </ImageUploading>
        </div>
        <div className={styles.tabText}>
          {isProcessing ? (
            <>
              {isLoading ? (
                <div className={styles.loaderContainer}>
                  <Loader themeColor="light" />
                </div>
              ) : (
                <div className={styles.textContainer}>
                  <h5>Image Description:</h5>
                  <div className={styles.result}>
                    {result?.productDescription}
                  </div>
                  <br />
                  <h6>
                    Recommendations (Powered by Azure OpenAI Services (DALL-E)):
                  </h6>
                  {result?.recommendedItems && (
                    <ScrollView
                      style={{
                        height: 400,
                        background: "transparent",
                        borderRadius: 8,
                      }}
                      automaticViewChangeInterval={2000}
                      endless={true}
                    >
                      {Object.keys(result?.recommendedItems).map(
                        (item, index) => {
                          return (
                            <div className={styles.imageWithText} key={index}>
                              <h6>{item}</h6>
                              <center>
                                <img
                                  src={result?.recommendedItems[item]}
                                  alt={item}
                                  className="img-thumbnail"
                                  draggable={false}
                                />
                              </center>
                            </div>
                          );
                        }
                      )}
                    </ScrollView>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ProductRecommendation;
